/**
 * BadgeCase — Premium badge showcase for the Overview page.
 *
 * Shows earned badges as vibrant SVG art with tier-based glows,
 * and locked badges as ghosted outlines. Tapping an earned badge
 * shows its title and description.
 *
 * Self-contained — reads quest completion data from useApp().
 */
import { useState, useMemo } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { useApp } from '../../hooks/useApp';
import { BADGES, TIER_GLOW, TIER_COLOR } from './badgeConfig';

export default function BadgeCase() {
  const { s, c } = useApp();
  const [selected, setSelected] = useState(null);

  const completed = s.questsCompleted || {};

  const badgesWithStatus = useMemo(() =>
    BADGES.map((badge) => ({
      ...badge,
      earned: badge.questId in completed,
    })),
  [completed]);

  const earnedCount = badgesWithStatus.filter((b) => b.earned).length;
  const total = badgesWithStatus.length;

  const selectedBadge = selected
    ? badgesWithStatus.find((b) => b.id === selected)
    : null;

  return (
    <Card c={c} delay={0.10}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 20, flexWrap: "wrap", gap: 8,
      }}>
        <Heading ic={null} c={c}>
          <span style={{ fontSize: 17 }}>🏆</span> Badge Case
        </Heading>
        <div style={{
          padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: earnedCount === total
            ? (s.dark ? "rgba(255,215,0,0.12)" : "rgba(255,215,0,0.08)")
            : (s.dark ? "rgba(10,132,255,0.10)" : "rgba(10,132,255,0.06)"),
          color: earnedCount === total ? "#FFD700" : c.blue,
        }}>
          {earnedCount}/{total} earned
        </div>
      </div>

      {/* Badge grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
        gap: 10,
      }}>
        {badgesWithStatus.map((badge) => (
          <BadgeTile
            key={badge.id}
            badge={badge}
            c={c}
            s={s}
            isSelected={selected === badge.id}
            onSelect={() => setSelected(selected === badge.id ? null : badge.id)}
          />
        ))}
      </div>

      {/* Detail panel for selected badge */}
      {selectedBadge && (
        <div style={{
          marginTop: 16, padding: "14px 18px", borderRadius: 14,
          background: s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
          border: `1px solid ${s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          animation: "fadeSlideUp 0.25s ease",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          {/* Mini badge art */}
          <div style={{ width: 44, height: 44, flexShrink: 0, opacity: selectedBadge.earned ? 1 : 0.3 }}>
            {selectedBadge.art(c)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 14, fontWeight: 700, color: c.fg,
              letterSpacing: "-0.01em", marginBottom: 2,
            }}>
              {selectedBadge.title}
              <span style={{
                marginLeft: 8, fontSize: 9, fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase",
                color: TIER_COLOR[selectedBadge.tier] || c.muted,
                opacity: 0.8,
              }}>
                {selectedBadge.tier}
              </span>
            </div>
            <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.4 }}>
              {selectedBadge.earned
                ? selectedBadge.desc
                : "Complete the quest to unlock this badge."
              }
            </div>
          </div>
          {selectedBadge.earned && (
            <div style={{
              fontSize: 10, fontWeight: 600, color: c.green,
              flexShrink: 0,
            }}>
              Earned ✓
            </div>
          )}
        </div>
      )}

      {/* Encouragement */}
      {earnedCount === 0 && (
        <div style={{
          marginTop: 16, fontSize: 13, color: c.muted,
          textAlign: "center", lineHeight: 1.5,
        }}>
          Complete quests to unlock premium badges.
          <br />
          <span style={{ fontSize: 12, opacity: 0.6 }}>Your collection starts with your first claim.</span>
        </div>
      )}
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   BADGE TILE — individual badge in the grid
   ═══════════════════════════════════════════════════ */

function BadgeTile({ badge, c, s, isSelected, onSelect }) {
  const { earned, tier, art } = badge;
  const glow = TIER_GLOW[tier] || TIER_GLOW.bronze;
  const tierColor = TIER_COLOR[tier] || c.muted;

  const glowShadow = earned
    ? `0 0 ${glow.blur}px ${glow.spread}px ${tierColor}${Math.round(glow.opacity * 255).toString(16).padStart(2, "0")}`
    : "none";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      style={{
        width: "100%",
        aspectRatio: "1",
        borderRadius: 14,
        background: earned
          ? (s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)")
          : (s.dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
        border: `1.5px solid ${
          isSelected
            ? (earned ? tierColor : c.border)
            : "transparent"
        }`,
        boxShadow: isSelected && earned ? glowShadow : "none",
        cursor: "pointer",
        padding: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.25s cubic-bezier(0.25,0.1,0.25,1)",
        opacity: earned ? 1 : 0.25,
        filter: earned ? "none" : "grayscale(1)",
        userSelect: "none",
      }}
    >
      <div style={{ width: "100%", height: "100%" }}>
        {art(earned ? c : { ...c, blue: c.muted, green: c.muted, orange: c.muted, red: c.muted, purple: c.muted, fg: c.muted })}
      </div>
    </div>
  );
}
